import { useEffect, useState } from 'react';

import { Button, FieldLabel, Modal, showSnackbar, TextArea } from 'fluxo-ui';
import { CheckCircleIcon, TimesIcon } from 'fluxo-ui/icons';

import { Badge, Card, EmptyState, PageHeader } from '../components/common';
import QuestionPreview from '../components/question/QuestionPreview';
import questionService from '../services/question.service';
import { useAuthStore } from '../store/authStore';
import { formatDateTime } from '../utils/formatting';

const QuestionsPendingPage: React.FC = () => {
    const { user } = useAuthStore();
    const [questions, setQuestions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [previewQuestion, setPreviewQuestion] = useState<any | null>(null);
    const [rejectQuestion, setRejectQuestion] = useState<any | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        if (user && ['super_admin', 'org_admin', 'org_moderator'].includes(user.role)) {
            fetchQuestions();
        }
    }, [user]);

    const fetchQuestions = async () => {
        try {
            setLoading(true);
            const response = await questionService.getPendingQuestions();
            setQuestions(response.questions);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to fetch questions', 'Error', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (questionId: string) => {
        try {
            await questionService.approveQuestion(questionId);
            showSnackbar('Question approved successfully', 'Success', { type: 'success' });
            fetchQuestions();
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to approve question', 'Error', { type: 'error' });
        }
    };

    const handleReject = async () => {
        if (!rejectQuestion) return;
        setActionLoading(true);
        try {
            await questionService.rejectQuestion(rejectQuestion._id, rejectReason.trim() || undefined);
            showSnackbar('Question rejected', 'Success', { type: 'success' });
            setRejectQuestion(null);
            setRejectReason('');
            fetchQuestions();
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to reject question', 'Error', { type: 'error' });
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Questions Pending Approval"
                description="Review and approve questions submitted by instructors"
                className="mb-0"
            />

            <Card padding="none">
                {loading ? (
                    <div className="p-8 text-center text-slate-500 dark:text-slate-400">Loading...</div>
                ) : questions.length === 0 ? (
                    <EmptyState
                        icon={CheckCircleIcon}
                        title="All caught up"
                        description="No questions pending approval right now."
                    />
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {questions.map((q: any) => (
                            <div key={q._id} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-medium text-slate-900 dark:text-white line-clamp-2">{q.title || q.text}</h3>
                                        <div className="flex flex-wrap items-center gap-2 mt-2">
                                            <Badge tone="accent">{(q.type || 'unknown').replace(/_/g, ' ')}</Badge>
                                            <Badge tone="neutral">{q.difficulty || '-'}</Badge>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <span className="hidden sm:inline text-sm text-slate-500 dark:text-slate-400">
                                            {formatDateTime(q.createdAt)}
                                        </span>
                                        <Button
                                            label="View"
                                            size="sm"
                                            variant="default"
                                            layout="outlined"
                                            onClick={() => setPreviewQuestion(q)}
                                        />
                                        <div className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-1.5 py-1">
                                            <Button
                                                leftIcon={CheckCircleIcon}
                                                size="sm"
                                                variant="success"
                                                layout="plain"
                                                onClick={() => handleApprove(q._id)}
                                                title="Approve"
                                            />
                                            <span className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
                                            <Button
                                                leftIcon={TimesIcon}
                                                size="sm"
                                                variant="danger"
                                                layout="plain"
                                                onClick={() => {
                                                    setRejectReason('');
                                                    setRejectQuestion(q);
                                                }}
                                                title="Reject"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Preview Modal */}
            <Modal isOpen={!!previewQuestion} onClose={() => setPreviewQuestion(null)} title="Question Preview" size="lg">
                {previewQuestion && (
                    <div className="p-6">
                        <QuestionPreview question={previewQuestion} showAnswer onClose={() => setPreviewQuestion(null)} />
                    </div>
                )}
            </Modal>

            {/* Reject Modal */}
            <Modal isOpen={!!rejectQuestion} onClose={() => !actionLoading && setRejectQuestion(null)} title="Reject Question">
                <div className="p-4 space-y-4">
                    <p className="text-slate-700 dark:text-slate-300">
                        Rejecting <strong>{rejectQuestion?.title || rejectQuestion?.text}</strong>. The author will see your feedback.
                    </p>
                    <FieldLabel label="Reason" hint="Explain what needs to change (optional)">
                        <TextArea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.value)}
                            placeholder="e.g. The correct answer is marked wrong, please review option B"
                            rows={4}
                        />
                    </FieldLabel>
                    <div className="flex justify-end gap-2">
                        <Button
                            label="Cancel"
                            variant="default"
                            layout="outlined"
                            onClick={() => setRejectQuestion(null)}
                            disabled={actionLoading}
                        />
                        <Button label="Reject" variant="danger" onClick={handleReject} isLoading={actionLoading} />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default QuestionsPendingPage;
